import React, { type FC, useState, useEffect } from 'react'
import { styled } from '@linaria/react'
import { FormattedMessage } from 'react-intl'
import { Typography } from '../ui/Typography'
import { ButtonRegular } from '../ui/Button'
import { colors } from '../styles/colors'
import VoteIoClassic from '../svgr/VoteIoClassic'
import { AmountInputWithPresets } from '../components/AmountInputWithPresets'
import { ActionRejectedDialog } from './ActionRejectedDialog'
import { VoteSuccessfullyAccepted } from './VoteSuccessfullyAccepted'
import { useNavigate } from 'react-router'
import { profileVotesPagePath } from '../pages/ProfilePage'
import { indexPagePath } from '../pages/IndexPage'
import { faqPagePath } from '../pages/FaqPage'
import { fromNano, type OpenedContract } from '@ton/core'
import type { BoxV0 } from '@voteboxio/core'
import { useVoteEstimation } from '../hooks/useVoteEstimation'
import { alignLeftDialogContentCss, DialogContainer } from './common.styles'
import {
  trackVoteInitiated,
  trackVoteSuccess,
  trackVoteFailed,
  trackVoteAmountEntered,
} from '../utils/amplitude'

const PRESET_AMOUNTS = [1, 5, 10, 25, 50]

export const enterVoteAmountDialogPath = '/enter-vote-amount-dialog'

export const EnterVoteAmountDialog: FC<{
  isValidVotingAmount: (amount: string) => boolean
  onClose: () => void
  onSubmit: (amount: string) => Promise<{ isSuccess: boolean; error?: string }>
  maxValue: string
  minValue: string
  boxContract?: OpenedContract<BoxV0.BoxV0>
  optionIndex: number
  boxId: string
}> = ({
  onClose,
  onSubmit,
  isValidVotingAmount,
  maxValue,
  minValue,
  boxContract,
  optionIndex,
  boxId,
}) => {
  const navigate = useNavigate()
  const [amount, setAmount] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const {
    estimatedVotes,
    isLoading: isEstimating,
    error: estimationError,
    getVoteEstimation,
  } = useVoteEstimation(boxContract, optionIndex)

  // Trigger estimation when amount changes
  useEffect(() => {
    getVoteEstimation(amount)
  }, [amount, getVoteEstimation, optionIndex])

  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount)
  }

  const handleConfirm = async () => {
    setIsLoading(true)
    const amountNum = parseFloat(amount)
    const isPreset = PRESET_AMOUNTS.includes(amountNum)
    trackVoteAmountEntered(boxId, amountNum, isPreset)
    trackVoteInitiated(boxId, optionIndex)

    const result = await onSubmit(amount)

    if (result.isSuccess) {
      trackVoteSuccess(boxId, optionIndex, amount)
      setIsError(false)
      setIsSuccess(true)
    } else {
      trackVoteFailed(boxId, optionIndex, result.error)
      setIsError(true)
      setIsSuccess(false)
    }

    setIsLoading(false)
  }

  if (isSuccess) {
    return (
      <VoteSuccessfullyAccepted
        onViewMyVotedBoxes={() => {
          onClose()
          navigate(profileVotesPagePath)
        }}
        toMainPage={() => {
          onClose()
          navigate(indexPagePath)
        }}
        amountToVote={parseFloat(amount)}
      />
    )
  }

  if (isError) {
    return (
      <ActionRejectedDialog isLoading={isLoading} onRetry={handleConfirm} />
    )
  }

  const isAmountMoreThanMax = Number(amount) > Number(fromNano(maxValue))
  const isAmountLessThanMin = Number(fromNano(minValue)) > Number(amount)

  return (
    <DialogContainer className={alignLeftDialogContentCss}>
      <IconContainer>
        <VoteIoClassic />
      </IconContainer>

      <TitleContainer>
        <Typography fontSize={20} fontWeight={700} color={colors.secondary}>
          <FormattedMessage
            id="dialog.enterBetAmount.title"
            defaultMessage="Введите TON для голосования"
          />
        </Typography>
      </TitleContainer>

      <InputContainer>
        <StyledAmountInputWithPresets
          data-testid="vote-amount-input"
          value={amount}
          onChange={handleAmountChange}
          maxValue={maxValue}
          minValue={minValue}
        />

        {isAmountMoreThanMax && (
          <Typography fontSize={12} color={colors.danger}>
            <FormattedMessage
              id="dialog.maxAmount"
              defaultMessage="Максимум {maxAmount} TON"
              values={{
                maxAmount: fromNano(maxValue),
              }}
            />
          </Typography>
        )}

        {isAmountLessThanMin && (
          <Typography fontSize={12} color={colors.danger}>
            <FormattedMessage
              id="dialog.minAmount"
              defaultMessage="Минимум {minAmount} TON"
              values={{
                minAmount: fromNano(minValue),
              }}
            />
          </Typography>
        )}

        {/* Vote Estimation Display */}
        {amount && parseFloat(amount) > 0 && (
          <>
            <EstimationContainer data-testid="estimated-votes">
              {isEstimating && (
                <Typography fontSize={12} color={colors.textSecondary}>
                  <FormattedMessage
                    id="dialog.estimating"
                    defaultMessage="Расчёт голосов..."
                  />
                </Typography>
              )}

              {estimatedVotes !== null && !isEstimating && (
                <Typography fontSize={12} color={colors.grey900}>
                  <FormattedMessage
                    id="dialog.estimatedVotes"
                    defaultMessage="Ожидаемое количество голосов: ~{votes}"
                    values={{
                      votes: Number(fromNano(estimatedVotes))
                        .toFixed(1)
                        .replace(/\.0$/, ''),
                    }}
                  />
                </Typography>
              )}

              {estimationError && !isEstimating && (
                <Typography fontSize={12} color={colors.warning}>
                  <FormattedMessage
                    id="dialog.estimationError"
                    defaultMessage="Не удалось рассчитать количество голосов"
                  />
                </Typography>
              )}
            </EstimationContainer>

            <DynamicVotingPowerInfo>
              <Typography fontSize={12} color={colors.textSecondary}>
                <FormattedMessage
                  id="dialog.dynamicVotingPower.summary"
                  defaultMessage="Сила голоса уменьшается ближе к завершению голосования. Голосуйте раньше — получайте больше голосов."
                />{' '}
                <MoreLink
                  onClick={async () =>
                    await navigate(`${faqPagePath}?section=dynamicVotingPower`)
                  }
                >
                  <FormattedMessage
                    id="dialog.dynamicVotingPower.more"
                    defaultMessage="Подробнее"
                  />
                </MoreLink>
              </Typography>
            </DynamicVotingPowerInfo>
          </>
        )}
      </InputContainer>

      <ConfirmButton
        onClick={handleConfirm}
        disabled={
          !amount ||
          parseFloat(amount) <= 0 ||
          !isValidVotingAmount(amount) ||
          isAmountMoreThanMax ||
          isAmountLessThanMin
        }
        textColor={colors.white}
        textWeight={700}
        loading={isLoading}
      >
        <FormattedMessage id="dialog.confirm" defaultMessage="Подтвердить" />
      </ConfirmButton>
    </DialogContainer>
  )
}

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: 120px;
    height: auto;
  }
`

const TitleContainer = styled.div`
  text-align: center;
`

const InputContainer = styled.div`
  width: 100%;
`

const ConfirmButton = styled(ButtonRegular)`
  background: ${colors.secondary};
  width: 100%;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StyledAmountInputWithPresets = styled(AmountInputWithPresets)`
  margin-bottom: 6px;
`

const EstimationContainer = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: ${colors.bgSecondary};
  border-radius: ${colors.radiusCard};
  border: 1px solid ${colors.borderSecondary};
`

const DynamicVotingPowerInfo = styled.div`
  margin-top: 8px;
`

const MoreLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  color: ${colors.textSecondary};
`
